import { useRef, useState } from "react";

/**
 * Writes a string to the clipboard, falling back to a hidden textarea +
 * `execCommand` when the async Clipboard API is unavailable or rejects (e.g.
 * the document is not focused).
 */
export const copyToClipboard = async (value: string) => {
  if (navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Clipboard API can fail when the document is not focused; fall through.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export interface UseCopyToClipboardResult {
  /** True for `resetDelayMs` after the most recent successful copy. */
  copied: boolean;
  /** Copies `value` and flips `copied` true for `resetDelayMs`. */
  copy: (value: string) => Promise<void>;
}

/**
 * Copies a string to the clipboard and exposes a `copied` flag that flips true
 * for `resetDelayMs` after each copy. Use directly when the copy affordance is
 * not a button (e.g. a chip or input adornment); for buttons prefer
 * IgCopyButton / IgCopyIconButton, which build on this hook.
 */
export const useCopyToClipboard = (
  resetDelayMs = 2000
): UseCopyToClipboardResult => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const copy = async (value: string) => {
    await copyToClipboard(value);
    setCopied(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setCopied(false), resetDelayMs);
  };

  return { copied, copy };
};
